import { Phone, Mail } from 'lucide-react';
import { getCdnBaseUrl } from '@/core/utils/apiUtils';
import FollowUs from '@/shared/components/FollowUs';
import { NewsletterSubscribe } from '@/shared/features/newsletter/presentation/components/NewsletterSubscribe';
import { WeAccept } from '@/shared/widgets/we-accept';
import footerSectionsData from '../data/footerSections.json';
import {
  COMPANY_CIN,
  CURRENT_YEAR,
  CUSTOMER_CARE_EMAIL,
  CUSTOMER_CARE_MAILTO,
  CUSTOMER_CARE_TEL,
} from '@/shared/constants/contact';

interface FooterItem {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  items: FooterItem[];
  isLink?: boolean;
}

interface FooterColumn {
  title: string;
  links: FooterItem[];
}

const footerSections: FooterSection[] = footerSectionsData as FooterSection[];

const footerColumns: FooterColumn[] = footerSections
  .filter((section) => !section.isLink)
  .map((section) => ({
    title: section.title,
    links: section.items,
  }));

export default function FooterDesktop() {
  return (
    <footer className="bg-[#001e38] text-white">
      <div className="container-fluid py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-[1fr_1fr_1fr_1.3fr] gap-6 mb-8">
          {/* Column 1: ABOUT US + CUSTOMER DELIGHT */}
          <div>
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-4 uppercase">ABOUT US</h3>
              <ul className="space-y-0">
                {footerColumns[0].links.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href || '#'}
                      className="text-sm text-white/80 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-4 uppercase">CUSTOMER DELIGHT</h3>
              <ul className="space-y-0">
                <li>
                  <a
                    href="/contact.html"
                    className="text-sm text-white/80 hover:text-white transition-colors"
                  >
                    Contact Us
                  </a>
                </li>
                <li>
                  <a
                    href="/faq.html"
                    className="text-sm text-white/80 hover:text-white transition-colors"
                  >
                    FAQ
                  </a>
                </li>
                <li className="flex items-center gap-2 text-sm text-white/80">
                  <Phone className="h-4 w-4" />
                  <a href={CUSTOMER_CARE_TEL} className="hover:text-white transition-colors">
                    1800-419-0066
                  </a>
                </li>
                <li className="flex items-center gap-2 text-sm text-white/80">
                  <Mail className="h-4 w-4" />
                  <a
                    href={CUSTOMER_CARE_MAILTO}
                    className="hover:text-white transition-colors"
                  >
                    {CUSTOMER_CARE_EMAIL}
                  </a>
                </li>
                <li className="text-sm text-white/80">
                  (9 am-10 pm, 7 days a week)
                </li>
              </ul>
            </div>
          </div>

          {/* Column 2: POLICIES + SHOP WITH CONFIDENCE */}
          <div>
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-4 uppercase">POLICIES</h3>
              <ul className="space-y-0">
                {footerColumns[1].links.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href || '#'}
                      className="text-sm text-white/80 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-4 uppercase">SHOP WITH CONFIDENCE</h3>
              <ul className="space-y-0">
                {footerColumns[2].links.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href || '#'}
                      className="text-sm text-white/80 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 3: JEWELLERY GUIDE */}
          <div>
            <h3 className="text-sm font-semibold mb-4 uppercase">JEWELLERY GUIDE</h3>
            <ul className="space-y-0">
              {footerColumns[3].links.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href || '#'}
                    className="text-sm text-white/80 hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: App Download */}
          <div>
            <h3 className="text-sm font-semibold mb-4 uppercase">Download BlueStone App</h3>
            <p className="text-xs text-white/80 mb-4">
              Shining new app, made just for you! It's Free, Easy & Smart.
            </p>
            <div className="flex gap-3 mb-6">
              <a
                href="https://play.google.com/store/apps/details?id=com.bluestone.android&referrer=website&utm_source%3Dwebsite_footer%26utm_medium%3Dwebsite%26utm_campaign%3Dwebsite"
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="Download on Google Play"
                className="bg-transparent hover:opacity-80 p-0 transition-opacity inline-flex"
              >
                <img src={`${getCdnBaseUrl()}assets/gplay.svg`} alt="Google Play" className="h-10 w-auto" />
              </a>
              <a
                href="https://itunes.apple.com/in/app/bluestone-jewellery-online/id1107631430?mt=8"
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="Download on the App Store"
                className="bg-transparent hover:opacity-80 p-0 transition-opacity inline-flex"
              >
                <img src={`${getCdnBaseUrl()}assets/appstore.svg`} alt="App Store" className="h-10 w-auto" />
              </a>
            </div>
            <div className="mt-10">
              <h3 className="text-sm font-semibold mb-4 uppercase">Subscribe to our Newsletter</h3>
              <NewsletterSubscribe
                onSubmit={(email) => {
                  console.log('Newsletter subscription:', email);
                  // Handle newsletter subscription here
                }}
              />
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Social Media */}
            <FollowUs variant="desktop" />

            {/* Payment Methods */}
            <WeAccept variant="desktop" />

            {/* Copyright */}
            <div className="text-sm text-white/80">
              <p>© {CURRENT_YEAR} BlueStone. All Rights Reserved.</p>
              <p className="text-xs">CIN: {COMPANY_CIN}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

